import { DefaultButton, Modal, Spinner, SpinnerSize } from "@fluentui/react";
import React, {
  Fragment,
  useContext,
  useImperativeHandle,
  useState,
} from "react";
import styled from "styled-components";
import { ApplyButton } from "./ApplyButton";
import { AvailableFieldsPane } from "./AvailableFieldsPane";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectedColumnsPane } from "./SelectedColumnsPane";
import { ViewDescription } from "./ViewDescription";
import { ViewSelect } from "./ViewSelect";
import { ViewToolbar } from "./ViewToolbar";
import { ViewDialog } from "./ViewDialog";

export type ViewDialogState = {
  isOpen: boolean;
  userAppSettingId: number | null;
};

const initStateViewDialog = {
  isOpen: false,
  userAppSettingId: null,
};

const modalStyles = {
  main: {
    width: "min(1400px, 92vw)",
    height: "88vh",
    maxWidth: "92vw",
    display: "flex",
    flexDirection: "column" as const,
  },
  scrollableContent: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    overflow: "hidden",
  },
};

export const ColumnOptionsPanel = () => {
  const [viewDialog, setViewDialog] =
    useState<ViewDialogState>(initStateViewDialog);
  const {
    isReady,
    closePanel,
    strings,
    disableButtons,
    columnOptionsRef,
    hasUnsavedChanges,
    t,
  } = useContext(ColumnOptionsContext);

  useImperativeHandle(columnOptionsRef, () => ({
    closeModal: () => {
      setViewDialog({ isOpen: false, userAppSettingId: null });
    },
  }));

  return (
    <Modal
      isOpen
      onDismiss={closePanel}
      isBlocking
      styles={modalStyles as any}
      className="column-options-modal"
    >
      <Container>
        <TopBar>
          <Title>{strings.headerLabel}</Title>
          <ViewSelectWrap>
            <ViewSelect />
          </ViewSelectWrap>
          {hasUnsavedChanges && (
            <UnsavedBadge
              title={t(
                "greco.unsavedChangesHint",
                "You have unsaved changes to this view. Click “Save current view” to keep them."
              )}
            >
              <Dot />
              {t("greco.unsavedChanges", "Unsaved changes")}
            </UnsavedBadge>
          )}
          <ViewDescription />
        </TopBar>
        <ToolbarRow>
          <ViewToolbar
            setViewDialog={setViewDialog}
            disableButtons={disableButtons}
          />
        </ToolbarRow>

        {!isReady ? (
          <SpinnerContainer>
            <Spinner size={SpinnerSize.large} />
          </SpinnerContainer>
        ) : (
          <Fragment>
            <Body>
              <LeftPane>
                <AvailableFieldsPane />
              </LeftPane>
              <Divider />
              <RightPane>
                <SelectedColumnsPane />
              </RightPane>
            </Body>
            {viewDialog.isOpen && (
              <ViewDialog
                userAppSettingId={viewDialog.userAppSettingId}
                close={() => setViewDialog(initStateViewDialog)}
              />
            )}
          </Fragment>
        )}

        <Footer>
          <DefaultButton
            text={t("greco.close", "Close")}
            onClick={closePanel}
          />
          <ApplyButton />
        </Footer>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 8px 16px;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.neutralLight};
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
`;

const UnsavedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme.palette.themeDarker};
  background: ${(props) => props.theme.palette.themeLighter};
  border: 1px solid ${(props) => props.theme.palette.themeLight};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.theme.palette.themePrimary};
`;

const ViewSelectWrap = styled.div`
  flex: 0 0 280px;
  width: 280px;
  min-width: 0;
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  padding: 12px 16px;
  gap: 12px;
  overflow: hidden;
`;

const LeftPane = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPane = styled.div`
  flex: 1;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid ${(props) => props.theme.palette.neutralLight};
  padding-left: 12px;
`;

const Divider = styled.div`
  width: 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
